import ReactiveDialog from "../../common/ReactiveDialog";
import { Box, Typography, TextField, InputAdornment } from "@mui/material";
import { useAddHours, useGetFamilyDetails } from "../../../services/advisor";
import Loading from "../../common/Loading";
import { Family } from "protogen/advisors_service_pb";
import { PlanUtilization } from "protogen/common_pb";
import { useEffect, useState } from "react";

type Props = {
  familyRef: string;
  open: boolean;
  setOpen: (open: boolean) => void;
  onUpdate: (family: Family) => void;
};

type AddHoursContentProps = {
  family: Family;
  planUtilization?: PlanUtilization;
  onHoursChange: (hours: number) => void;
  onPriceChange: (price: number) => void;
  hours: number;
  price: number;
};

const AddHoursContent = ({
  family,
  planUtilization,
  onHoursChange,
  onPriceChange,
  hours,
  price,
}: AddHoursContentProps) => {
  const nextBillingDateStr = new Date(
    planUtilization?.periodEndDate || 0,
  ).toLocaleDateString("en-US", {
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });

  const totalCharge = price;
  const ratePerHour = hours > 0 ? totalCharge / hours : 0;

  return (
    <Box display="flex" flexDirection="column" gap="12px">
      <Typography variant="body">
        Apply a one time charge to add hours to the{" "}
        <strong>{family.name}</strong> family.
      </Typography>
      <TextField
        label="Total charge"
        type="number"
        value={totalCharge}
        onChange={(e) => onPriceChange(Number(e.target.value))}
        fullWidth
        InputProps={{
          startAdornment: (
            <InputAdornment
              position="start"
              sx={{
                margin: "0 0 0 20px",
                paddingTop: "16px",
              }}
            >
              $
            </InputAdornment>
          ),
        }}
        inputProps={{ min: 0, step: 10 }}
        sx={{
          "& .MuiInputBase-input": {
            padding: "0 0 0 6px",
          },
        }}
      />
      <TextField
        label="Number of hours"
        type="number"
        value={hours}
        onChange={(e) => onHoursChange(Number(e.target.value))}
        fullWidth
        inputProps={{ min: 0, step: 0.5 }}
      />
      <Box display="flex" flexDirection="column" gap="4px">
        <Typography variant="body" color="text.secondary">
          <strong>Rate per hour: ${ratePerHour.toFixed(2)}/hr</strong>
        </Typography>
        <Typography variant="body">
          The client will be charge immediately and the hours will be added to
          the current billing period which ends on{" "}
          <strong>{nextBillingDateStr}</strong>.
        </Typography>
      </Box>
    </Box>
  );
};

export default ({ familyRef, open, setOpen, onUpdate }: Props) => {
  const {
    data: familyData,
    request: requestFamily,
    loading: loadingFamily,
  } = useGetFamilyDetails();
  const [hours, setHours] = useState(2);
  const [price, setPrice] = useState(100);
  const { request: requestAddHours, loading: loadingAddHours } = useAddHours();

  useEffect(() => {
    if (open) {
      requestFamily({ familyRef });
    }
  }, [open, familyRef]);

  const onAddHours = async () => {
    if (loadingAddHours) return;
    if (!(hours && price)) return;
    const resp = await requestAddHours({
      familyRef,
      hours,
      ammountCents: price * 100,
    });
    if (resp?.family) {
      onUpdate(resp.family);
      setOpen(false);
    }
  };

  return (
    <ReactiveDialog
      open={open}
      onClose={() => setOpen(false)}
      title="Bill for extra hours"
      primaryActionName="Charge client"
      primaryActionEnabled={
        !loadingAddHours &&
        !loadingFamily &&
        !!familyData?.family &&
        hours > 0 &&
        price > 0
      }
      primaryAction={onAddHours}
      fullWidthSize="sm"
    >
      {loadingFamily ? (
        <Box display="flex" justifyContent="center" p={3}>
          <Loading />
        </Box>
      ) : familyData?.family ? (
        <AddHoursContent
          family={familyData.family}
          planUtilization={familyData.planUtilization}
          onHoursChange={setHours}
          onPriceChange={setPrice}
          hours={hours}
          price={price}
        />
      ) : null}
    </ReactiveDialog>
  );
};
